import React from 'react';
import AppleLogin from 'react-apple-login';
import { setAuthToken, API_BASE_URL } from '../../api/config';
import { runtimeValue } from '../../api/runtime-config';
import { getMe } from '../../api/endpoints';
import { navigateTo } from '../../utils/navigation';

interface AppleAuthorization {
  id_token?: string;
}

interface AppleUserName {
  firstName?: string;
  lastName?: string;
}

interface AppleUser {
  name?: AppleUserName;
}

interface AppleLoginResponse {
  error?: unknown;
  authorization?: AppleAuthorization;
  user?: AppleUser;
}

interface AppleRenderProps {
  onClick: () => void;
  disabled?: boolean;
}

const AppleIcon = () => (
    <svg className="w-[18px] h-[18px] mb-[3px] mr-2" viewBox="0 0 384 512" fill="currentColor">
        <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 48.6-25.2 80.7 25 1.6 49.9-18.8 69-43.1z" />
    </svg>
);

async function handleAppleLoginResponse(response: AppleLoginResponse | undefined) {
    console.log('[AppleDebug] responseHandler called', response);

    if (!response) {
        console.error("[AppleDebug] Apple Signin Error", response);
        return;
    }

    if (response.error) {
        console.error("[AppleDebug] Apple Signin Error", response);
        return;
    }

    const { authorization, user } = response;
    if (!authorization?.id_token) {
        console.error('[AppleDebug] No authorization or id_token in response', response);
        return;
    }

    try {
        const payload: { id_token: string; name?: string } = { id_token: authorization.id_token };
        if (user?.name) {
            console.log('[AppleDebug] User object present', user);
            payload.name = `${user.name.firstName ?? ''} ${user.name.lastName ?? ''}`.trim();
        }

        console.log('[AppleDebug] Sending payload to backend', payload);

        const res = await fetch(`${API_BASE_URL}auth/apple-login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });

        if (!res.ok) {
            const errText = await res.text();
            console.error('[AppleDebug] Apple login failed:', res.status, errText);
            throw new Error('Apple login failed');
        }

        const data = (await res.json()) as { 'jwt-key'?: string };
        console.log('[AppleDebug] Backend success, JWT received');
        const token = data['jwt-key'];
        if (!token) throw new Error('Apple login failed');
        setAuthToken(token);

        try {
            await getMe();
            console.log('[AppleDebug] getMe success, navigating to /');
            navigateTo('/');
        } catch (error) {
            console.error("[AppleDebug] Apple Login: Failed to fetch me", error);
            navigateTo('/');
        }

    } catch (error) {
        console.error("[AppleDebug] Backend Apple Login Failed", error);
    }
}

export default function AppleLoginButton() {
    const clientId = process.env.REACT_APP_APPLE_CLIENT_ID ?? 'skin.kivo.backend';
    // Domain-dependent: signing in on dev.kivo.skin must come back to
    // dev.kivo.skin, not to production. Falls back to the current origin so a
    // new domain works without a rebuild. NOTE: Apple rejects a redirect URI
    // that is not registered on the Service ID, so each new host still has to
    // be added in the Apple developer console.
    const redirectURI =
        runtimeValue('APPLE_REDIRECT_URI') ??
        process.env.REACT_APP_APPLE_REDIRECT_URI ??
        (typeof window !== 'undefined' ? window.location.origin : 'https://kivo.skin');

    return (
        <div className="w-full flex justify-center">
            <AppleLogin
                clientId={clientId}
                redirectURI={redirectURI}
                usePopup={true}
                scope="name email"
                responseMode="query"
                callback={(response) => { void handleAppleLoginResponse(response as AppleLoginResponse); }}
                render={(renderProps: AppleRenderProps) => (
                    <button
                        type="button"
                        onClick={() => {
                            console.log('[AppleDebug] Link button clicked');
                            renderProps.onClick();
                        }}
                        disabled={renderProps.disabled}
                        className="flex items-center justify-center bg-black text-white rounded-full transition-colors hover:bg-[#333333] active:bg-[#000000]"
                        style={{
                            width: '100%',
                            maxWidth: '340px',
                            height: '40px',
                            fontSize: '15px',
                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                            fontWeight: 500,
                        }}
                    >
                        <AppleIcon />
                        <span>Sign in with Apple</span>
                    </button>
                )}
            />
        </div>
    );
}
